// Collection Launch Performance — "is this launch tracking like a winner?"
// Every collection whose FIRST INVOICE falls in the chosen cohort gets a
// cumulative-revenue ramp indexed by months-since-launch and compared to the
// cohort's P25/P50/P75 band at the same age, so a 4-month-old launch is judged
// against how every other launch looked at 4 months — not against MADISON.
// Catalog-created collections that have never invoiced are listed separately.
//
// Backend: CollectionLaunchesController (api/CollectionLaunches/*), warehouse
// revenue = ExtPrice, launch = first invoice month, cohort payload cached 60m.
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, Button, IconButton, TextField, MenuItem,
  Tooltip as MuiTooltip, CircularProgress, Skeleton, Alert, InputAdornment,
  Table, TableHead, TableBody, TableRow, TableCell, Select, Drawer, Avatar,
  Collapse,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  MdRocketLaunch, MdRefresh, MdFileDownload, MdSearch, MdClose, MdExpandMore,
  MdExpandLess, MdShowChart, MdFilterListOff,
} from 'react-icons/md';
import {
  ResponsiveContainer, ComposedChart, Area, Line, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip,
} from 'recharts';
import * as XLSX from 'xlsx';
import { API_ENDPOINTS, apiCall } from '../config/api';

const ACCENT = '#7C4DFF';
// Okabe–Ito hues, assigned to selected collections in fixed order
const LINE_COLORS = ['#0072B2', '#E69F00', '#009E73', '#CC79A7'];

const usd0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const usdC = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 });
const num0 = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const money = (n, compact = false) => (compact ? usdC : usd0).format(Number(n) || 0);
const count = (n) => num0.format(Number(n) || 0);
const clean = (s) => String(s ?? '').split(String.fromCharCode(0)).join('').trim();

const STATUS = {
  'just-launched': { label: 'Just launched', color: '#0072B2', desc: 'first invoice this month — too early to judge' },
  outperforming: { label: 'Outperforming', color: '#009E73', desc: '≥150% of the cohort median at the same age' },
  'on-track': { label: 'On track', color: '#4C9F70', desc: '75–150% of the cohort median at the same age' },
  'slow-start': { label: 'Slow start', color: '#E69F00', desc: '30–75% of the cohort median at the same age' },
  underperforming: { label: 'Underperforming', color: '#D55E00', desc: 'below 30% of the cohort median at the same age' },
  stalled: { label: 'Stalled', color: '#B71C1C', desc: 'no revenue at all in the last 3 months' },
};
const STATUS_ORDER = ['outperforming', 'on-track', 'slow-start', 'underperforming', 'stalled', 'just-launched'];

const SORTS = [
  { key: 'launch', label: 'Newest launch' },
  { key: 'totRev', label: 'Total revenue' },
  { key: 'm3', label: 'First 3 months' },
  { key: 'trailing3', label: 'Last 3 months' },
  { key: 'vsCohort', label: 'Vs cohort' },
  { key: 'items', label: 'Items' },
];

// recharts derives tooltip row colors from stroke/fill — the transparent band
// Areas would render invisible rows, so the ramp chart uses explicit content
function RampTooltip({ active, payload, label, dark, gridStroke }) {
  if (!active || !payload || !payload.length) return null;
  const get = (k) => payload.find((e) => e.dataKey === k);
  const p25 = get('p25');
  const band = get('band');
  const p50 = get('p50');
  const lines = payload.filter((e) => !['p25', 'band', 'p50'].includes(e.dataKey));
  const p75v = (Number(p25?.value) || 0) + (Number(band?.value) || 0);
  return (
    <Box sx={{ background: dark ? '#1e1e2f' : '#fff', border: `1px solid ${gridStroke}`, borderRadius: 2, p: 1, fontSize: 13 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>Month {label} since launch</Typography>
      {p25 && band && (
        <Typography variant="caption" sx={{ display: 'block', color: ACCENT }}>
          Cohort P25–P75: {usdC.format(Number(p25.value) || 0)} – {usdC.format(p75v)}
        </Typography>
      )}
      {p50 && (
        <Typography variant="caption" sx={{ display: 'block', color: ACCENT, fontWeight: 700 }}>
          Cohort median: {usdC.format(Number(p50.value) || 0)}
        </Typography>
      )}
      {lines.map((e) => (
        <Typography key={e.dataKey} variant="caption" sx={{ display: 'block', color: e.color || e.stroke, fontWeight: 700 }}>
          {e.dataKey}: {usdC.format(Number(e.value) || 0)}
        </Typography>
      ))}
    </Box>
  );
}

export default function CollectionLaunches() {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const yearNow = new Date().getFullYear();

  const [fromYear, setFromYear] = useState(2022);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);

  const [qText, setQText] = useState('');
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sort, setSort] = useState('launch');
  const [selected, setSelected] = useState([]); // collections drawn on the ramp chart
  const [showNoSales, setShowNoSales] = useState(false);

  const [drill, setDrill] = useState(null);       // collection name in the drawer
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState('');
  const [brokenThumbs, setBrokenThumbs] = useState(() => new Set());

  const abortRef = useRef(null);
  const drillAbort = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setQ(qText.trim()), 350);
    return () => clearTimeout(t);
  }, [qText]);

  // plotted selections only make sense within one cohort
  useEffect(() => { setSelected([]); }, [fromYear]);

  useEffect(() => {
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true); setError('');
    apiCall(`${API_ENDPOINTS.COLLECTION_LAUNCHES_OVERVIEW}?fromYear=${fromYear}`, { signal: ac.signal })
      .then((r) => { if (abortRef.current === ac) { setData(r); setLoading(false); } })
      .catch((e) => {
        if (e.name === 'AbortError' || abortRef.current !== ac) return;
        setError(e.message || 'Failed to load launches'); setLoading(false);
      });
    return () => ac.abort();
  }, [fromYear, refreshTick]);

  useEffect(() => {
    if (!drill) { setDrillData(null); setDrillError(''); return undefined; }
    if (drillAbort.current) drillAbort.current.abort();
    const ac = new AbortController();
    drillAbort.current = ac;
    setDrillLoading(true); setDrillError(''); setDrillData(null);
    apiCall(`${API_ENDPOINTS.COLLECTION_LAUNCHES_DETAIL}?collection=${encodeURIComponent(drill)}`, { signal: ac.signal })
      .then((r) => { if (drillAbort.current === ac) { setDrillData(r); setDrillLoading(false); } })
      .catch((e) => {
        if (e.name === 'AbortError' || drillAbort.current !== ac) return;
        setDrillError(e.message || 'Failed to load detail'); setDrillLoading(false);
      });
    return () => ac.abort();
  }, [drill]);

  const collections = useMemo(() => (data?.collections || []).map((c) => ({
    ...c, coll: clean(c.coll), mainType: clean(c.mainType), status: clean(c.status),
  })), [data]);
  const noSales = useMemo(() => (data?.noSales || []).map((c) => ({ ...c, coll: clean(c.coll), mainType: clean(c.mainType) })), [data]);
  const bands = data?.bands || [];

  const statusCounts = useMemo(() => {
    const m = {};
    collections.forEach((c) => { m[c.status] = (m[c.status] || 0) + 1; });
    return m;
  }, [collections]);

  const filtered = useMemo(() => {
    let rows = collections;
    if (q) rows = rows.filter((c) => c.coll.toLowerCase().includes(q.toLowerCase()) || c.mainType.toLowerCase().includes(q.toLowerCase()));
    if (statusFilter) rows = rows.filter((c) => c.status === statusFilter);
    const dir = sort === 'launch' ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (sort === 'launch') return String(b.launch).localeCompare(String(a.launch)) * dir || a.coll.localeCompare(b.coll);
      // vsCohort 999 is the backend's "no cohort median" sentinel — sort it last
      const num = (x) => { const v = Number(x[sort]) || 0; return sort === 'vsCohort' && v >= 999 ? -1 : v; };
      return num(b) - num(a) || a.coll.localeCompare(b.coll);
    });
  }, [collections, q, statusFilter, sort]);

  const kpis = useMemo(() => {
    const last12 = collections.filter((c) => c.age <= 12).length;
    const winners = (statusCounts.outperforming || 0);
    const trouble = (statusCounts['slow-start'] || 0) + (statusCounts.underperforming || 0) + (statusCounts.stalled || 0);
    const median3 = bands.find((b) => b.age === 2)?.p50 || 0;
    return { total: collections.length, last12, winners, trouble, noSales: noSales.length, median3 };
  }, [collections, statusCounts, bands, noSales]);

  // ramp chart data: cohort band + up to 4 selected collections
  const chartData = useMemo(() => {
    const byColl = new Map(collections.map((c) => [c.coll, c]));
    return bands.map((b) => {
      const row = { age: b.age, p25: b.p25, band: Math.max(0, (b.p75 || 0) - (b.p25 || 0)), p50: b.p50 };
      selected.forEach((name) => {
        const c = byColl.get(name);
        if (c && Array.isArray(c.curve) && b.age < c.curve.length) row[name] = c.curve[b.age];
      });
      return row;
    });
  }, [bands, selected, collections]);

  const toggleSelect = (coll) => {
    setSelected((prev) => (prev.includes(coll)
      ? prev.filter((x) => x !== coll)
      : (prev.length >= 4 ? [...prev.slice(1), coll] : [...prev, coll])));
  };

  const exportExcel = () => {
    const head = ['Collection', 'First Sale', 'Catalog Launch', 'Age (months)', 'Items', 'Active Items', 'Type',
      'Total Revenue', 'First 3 Months', 'First 6 Months', 'First 12 Months', 'Last 3 Months', 'Customers', 'Units', 'Last Sale', 'Vs Cohort Median', 'Status'];
    const body = filtered.map((c) => [c.coll, c.launch, c.catalogLaunch, Number(c.age) || 0, Number(c.items) || 0,
      Number(c.itemsActive) || 0, c.mainType, Number(c.totRev) || 0, Number(c.m3) || 0, Number(c.m6) || 0,
      Number(c.m12) || 0, Number(c.trailing3) || 0, Number(c.custs) || 0, Number(c.totUnits) || 0,
      clean(c.lastSale), Number(c.vsCohort) >= 999 ? '' : (Number(c.vsCohort) || 0), STATUS[c.status]?.label || c.status]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([head, ...body]), 'Launches');
    if (noSales.length) {
      const nsHead = ['Collection', 'Catalog Launch', 'Items', 'Type'];
      const nsBody = noSales.map((c) => [c.coll, clean(c.catalogLaunch), Number(c.items) || 0, c.mainType]);
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([nsHead, ...nsBody]), 'No Sales Yet');
    }
    XLSX.writeFile(wb, `Collection_Launches_${fromYear}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const gridStroke = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';
  const bandFill = dark ? 'rgba(124,77,255,0.18)' : 'rgba(124,77,255,0.12)';

  const drillMonthly = useMemo(() => (drillData?.monthly || []).map((m) => ({
    m: clean(m.m).slice(0, 7), rev: Number(m.rev) || 0, units: Number(m.units) || 0, custs: Number(m.custs) || 0,
  })), [drillData]);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1500, mx: 'auto' }}>
      {/* header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <MdRocketLaunch size={26} color={ACCENT} />
            <Typography variant="h5" sx={{ fontWeight: 800 }}>Collection Launch Performance</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Every collection first invoiced since {fromYear}, measured months-since-launch against the whole cohort.
            {data?.measurableFrom ? ` Sales history begins ${clean(data.dataFloor)}, so launches are measurable from ${clean(data.measurableFrom)} — anything selling before that is treated as pre-existing, not a launch.` : ''}
            {data?.asOf ? ` Data as of ${data.asOf}.` : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Select size="small" value={fromYear} onChange={(e) => setFromYear(Number(e.target.value))}>
            {Array.from({ length: yearNow - 2019 }, (_, i) => 2020 + i).map((y) => (
              <MenuItem key={y} value={y}>Launched {y}+</MenuItem>
            ))}
          </Select>
          <MuiTooltip title="Refresh"><span>
            <IconButton onClick={() => setRefreshTick((t) => t + 1)} disabled={loading}><MdRefresh /></IconButton>
          </span></MuiTooltip>
          <Button variant="outlined" size="small" startIcon={<MdFileDownload />} onClick={exportExcel} disabled={loading || !filtered.length}>
            Export
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* KPI tiles */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' }, gap: 1.5, mb: 2 }}>
        {[
          { label: `Launches since ${fromYear}`, value: count(kpis.total), hint: 'collections with a first invoice in the cohort' },
          { label: 'Launched last 12 mo', value: count(kpis.last12), hint: 'still in their ramp window' },
          { label: 'Outperforming', value: count(kpis.winners), hint: '≥150% of cohort median at the same age', color: '#009E73' },
          { label: 'Needs attention', value: count(kpis.trouble), hint: 'slow start, underperforming or stalled', color: '#D55E00' },
          { label: 'No sales yet', value: count(kpis.noSales), hint: 'in the catalog but never invoiced', color: '#E69F00' },
          { label: 'Median 3-mo revenue', value: money(kpis.median3, true), hint: 'cohort median cumulative revenue at month 3' },
        ].map((t) => (
          <Paper key={t.label} variant="outlined" sx={{ p: 1.5 }}>
            {loading ? <Skeleton width="60%" height={34} /> : (
              <Typography variant="h6" sx={{ fontWeight: 800, color: t.color }}>{t.value}</Typography>
            )}
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>{t.label}</Typography>
            <Typography variant="caption" color="text.secondary">{t.hint}</Typography>
          </Paper>
        ))}
      </Box>

      {/* ramp chart */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={1} sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <MdShowChart color={ACCENT} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Cumulative revenue by months since launch</Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Shaded band = middle half of all {count(kpis.total)} launches (P25–P75) · dashed = cohort median · click table rows to plot up to 4 collections
          </Typography>
        </Stack>
        {selected.length > 0 && (
          <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
            {selected.map((name, i) => (
              <Chip key={name} label={name} size="small" onDelete={() => toggleSelect(name)}
                sx={{ borderLeft: `4px solid ${LINE_COLORS[i % LINE_COLORS.length]}`, borderRadius: 1 }} />
            ))}
          </Stack>
        )}
        <Box sx={{ width: '100%', height: 320 }}>
          {loading ? <Skeleton variant="rounded" height={300} /> : (
            <ResponsiveContainer>
              <ComposedChart data={chartData} margin={{ top: 8, right: 16, bottom: 4, left: 8 }}>
                <CartesianGrid stroke={gridStroke} vertical={false} />
                <XAxis dataKey="age" tickFormatter={(v) => `M${v}`} tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(v) => money(v, true)} tick={{ fontSize: 12 }} width={70} />
                <Tooltip content={<RampTooltip dark={dark} gridStroke={gridStroke} />} />
                <Area dataKey="p25" stackId="band" stroke="none" fill="transparent" name="p25" isAnimationActive={false} />
                <Area dataKey="band" stackId="band" stroke="none" fill={bandFill} name="band" isAnimationActive={false} />
                <Line dataKey="p50" stroke={ACCENT} strokeDasharray="6 4" strokeWidth={2} dot={false} name="p50" isAnimationActive={false} />
                {selected.map((name, i) => (
                  <Line key={name} dataKey={name} stroke={LINE_COLORS[i % LINE_COLORS.length]} strokeWidth={2.5}
                    dot={{ r: 2 }} name={name} isAnimationActive={false} connectNulls={false} />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Paper>

      {/* filters */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <TextField
          size="small" placeholder="Search collection or type…" value={qText}
          onChange={(e) => setQText(e.target.value)} sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><MdSearch /></InputAdornment>,
            endAdornment: qText ? <InputAdornment position="end"><IconButton size="small" onClick={() => setQText('')}><MdClose size={16} /></IconButton></InputAdornment> : null,
          }}
        />
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          <Chip label={`All ${collections.length}`} size="small" variant={statusFilter === '' ? 'filled' : 'outlined'}
            onClick={() => setStatusFilter('')} color={statusFilter === '' ? 'primary' : 'default'} />
          {STATUS_ORDER.filter((s) => statusCounts[s]).map((s) => (
            <MuiTooltip key={s} title={STATUS[s].desc}>
              <Chip label={`${STATUS[s].label} ${statusCounts[s]}`} size="small"
                variant={statusFilter === s ? 'filled' : 'outlined'}
                onClick={() => setStatusFilter(statusFilter === s ? '' : s)}
                sx={{ borderColor: STATUS[s].color, ...(statusFilter === s ? { bgcolor: STATUS[s].color, color: '#fff' } : { color: STATUS[s].color }) }} />
            </MuiTooltip>
          ))}
        </Stack>
        <Box sx={{ flex: 1 }} />
        <Select size="small" value={sort} onChange={(e) => setSort(e.target.value)}>
          {SORTS.map((s) => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>)}
        </Select>
        {(q || statusFilter) && (
          <Button size="small" startIcon={<MdFilterListOff />} onClick={() => { setQText(''); setStatusFilter(''); }}>Clear</Button>
        )}
      </Stack>

      {/* launch table */}
      <Paper variant="outlined" sx={{ mb: 2, overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 1050 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Collection</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>First sale</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Age</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Items</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">First 3 mo</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Total rev</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Last 3 mo</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Customers</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Vs cohort</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && Array.from({ length: 8 }).map((_, i) => (
              <TableRow key={i}><TableCell colSpan={10}><Skeleton height={24} /></TableCell></TableRow>
            ))}
            {!loading && filtered.slice(0, 400).map((c) => {
              const sel = selected.includes(c.coll);
              const st = STATUS[c.status] || { label: c.status, color: '#888' };
              return (
                <TableRow key={c.coll} hover onClick={() => toggleSelect(c.coll)}
                  sx={{ cursor: 'pointer', ...(sel ? { bgcolor: dark ? 'rgba(124,77,255,0.12)' : 'rgba(124,77,255,0.07)' } : {}) }}>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{c.coll}</Typography>
                      {c.mainType && <Typography variant="caption" color="text.secondary">{c.mainType}</Typography>}
                      <Button size="small" sx={{ minWidth: 0, px: 0.5, fontSize: 11 }}
                        onClick={(e) => { e.stopPropagation(); setDrill(c.coll); setBrokenThumbs(new Set()); }}>
                        details
                      </Button>
                    </Stack>
                  </TableCell>
                  <TableCell>{c.launch}</TableCell>
                  <TableCell align="right">{c.age} mo</TableCell>
                  <TableCell align="right">{count(c.items)}</TableCell>
                  <TableCell align="right">{money(c.m3, true)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{money(c.totRev, true)}</TableCell>
                  <TableCell align="right">{money(c.trailing3, true)}</TableCell>
                  <TableCell align="right">{count(c.custs)}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 700, color: st.color }}>
                      {Number(c.vsCohort) >= 999 ? '—' : `${Math.round((Number(c.vsCohort) || 0) * 100)}%`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <MuiTooltip title={st.desc || ''}>
                      <Chip label={st.label} size="small" sx={{ bgcolor: `${st.color}22`, color: st.color, fontWeight: 700 }} />
                    </MuiTooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {!loading && !filtered.length && (
              <TableRow><TableCell colSpan={10}>
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  No launches match these filters.
                </Typography>
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
        {!loading && filtered.length > 400 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 1 }}>
            Showing the first 400 of {count(filtered.length)} — refine the search or use Export for the full list.
          </Typography>
        )}
      </Paper>

      {/* no-sales-yet */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1.5, cursor: 'pointer' }}
          onClick={() => setShowNoSales((v) => !v)}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            In the catalog, no sales yet ({count(noSales.length)})
          </Typography>
          <IconButton size="small">{showNoSales ? <MdExpandLess /> : <MdExpandMore />}</IconButton>
        </Stack>
        <Collapse in={showNoSales}>
          <Box sx={{ px: 1.5, pb: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 1 }}>
            {noSales.map((c) => (
              <Paper key={c.coll} variant="outlined" sx={{ p: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{c.coll}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {c.mainType || 'Unknown type'} · {count(c.items)} items · created {clean(c.catalogLaunch)}
                </Typography>
              </Paper>
            ))}
            {!noSales.length && <Typography variant="body2" color="text.secondary">Every cataloged collection in this cohort has invoiced.</Typography>}
          </Box>
        </Collapse>
      </Paper>

      {/* detail drawer */}
      <Drawer anchor="right" open={!!drill} onClose={() => setDrill(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, p: 2 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>{drill}</Typography>
          <IconButton onClick={() => setDrill(null)}><MdClose /></IconButton>
        </Stack>
        {drillLoading && <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>}
        {drillError && (
          <Alert severity="error" action={<Button size="small" onClick={() => { const c = drill; setDrill(null); setTimeout(() => setDrill(c), 0); }}>Retry</Button>}>
            {drillError}
          </Alert>
        )}
        {!drillLoading && !drillError && drillData && (
          <>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Monthly revenue</Typography>
            <Box sx={{ width: '100%', height: 180, mb: 2 }}>
              <ResponsiveContainer>
                <ComposedChart data={drillMonthly} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke={gridStroke} vertical={false} />
                  <XAxis dataKey="m" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tickFormatter={(v) => money(v, true)} tick={{ fontSize: 10 }} width={58} />
                  <Tooltip formatter={(v, n) => (n === 'rev' ? [money(v), 'Revenue'] : [count(v), n === 'units' ? 'Units' : 'Customers'])}
                    contentStyle={{ background: dark ? '#1e1e2f' : '#fff', border: `1px solid ${gridStroke}`, borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="rev" fill={ACCENT} radius={[3, 3, 0, 0]} isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Top items (lifetime)</Typography>
            <Stack spacing={0.5} sx={{ mb: 2 }}>
              {(drillData.topItems || []).map((it) => (
                <Stack key={clean(it.item)} direction="row" spacing={1} alignItems="center">
                  <Avatar variant="rounded" src={!brokenThumbs.has(clean(it.item)) && clean(it.thumb) ? clean(it.thumb) : undefined}
                    imgProps={{ onError: () => setBrokenThumbs((prev) => new Set(prev).add(clean(it.item))) }}
                    sx={{ width: 34, height: 34, fontSize: 11 }}>{clean(it.item).slice(0, 2)}</Avatar>
                  <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>{clean(it.item)}</Typography>
                  <Typography variant="body2">{money(it.rev, true)}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ width: 70, textAlign: 'right' }}>{count(it.units)} units</Typography>
                </Stack>
              ))}
              {!(drillData.topItems || []).length && <Typography variant="caption" color="text.secondary">No item sales recorded.</Typography>}
            </Stack>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Top customers (lifetime)</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Revenue</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Units</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>First / last buy</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(drillData.topCustomers || []).map((cu) => (
                  <TableRow key={clean(cu.id)}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{clean(cu.name) || clean(cu.id)}</Typography>
                      <Typography variant="caption" color="text.secondary">{clean(cu.id)}</Typography>
                    </TableCell>
                    <TableCell align="right">{money(cu.rev, true)}</TableCell>
                    <TableCell align="right">{count(cu.units)}</TableCell>
                    <TableCell><Typography variant="caption">{clean(cu.firstBuy)} → {clean(cu.lastBuy)}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </Drawer>
    </Box>
  );
}
